import { datadogRum } from '@datadog/browser-rum';
import { push } from 'connected-react-router';
import type { SagaIterator } from 'redux-saga';
import { call, getContext, put, takeEvery } from 'redux-saga/effects';
import { getScreenPropsSaga } from '@peloton/analytics/baseProperties';
import type { AxiosErrorWithMeta } from '@peloton/api/ClientError';
import type { Client } from '@peloton/api/utils';
import { CLIENT_CONTEXT } from '@peloton/api/utils';
import { browserProvider } from '@peloton/newrelic/browser-provider';
import { loadClassDetailSaga } from '@engage/class-detail/sagas';
import { loadClassSuccess } from '@engage/classes/redux';
import { logError } from '@engage/error-reporting/logError';
import type { SplitResponse } from './api/workout';
import { deleteWorkout, fetchWorkoutDetails } from './api/workout';
import { isClassWorkout } from './models/Workout';
import type { WorkoutRequestAction, WorkoutRequestSuccessAction } from './redux';
import { loadWorkoutSuccess, removeWorkoutSuccess, WorkoutActionType } from './redux';

export const loadWorkoutById = function* (client: Client, workoutId: string) {
  try {
    const { workout, klass } = yield call(fetchWorkoutDetails, client, workoutId);
    yield loadWorkoutAndClass({ workout, klass });
  } catch (e) {
    const axiosErr = e as AxiosErrorWithMeta;
    if (axiosErr.response?.status == 403) {
      const actionReporter = browserProvider.getActionReporter();
      actionReporter('Member viewed private legacy workout', {});
      yield put(push('/'));
    } else {
      throw e;
    }
  }
};

export const loadWorkoutSaga = function* (client: Client, action: WorkoutRequestAction) {
  yield call(loadWorkoutById, client, action.payload.id);
};

export const deleteWorkoutSaga = function* (
  client: Client,
  workoutId: string,
  source?: string,
): SagaIterator {
  try {
    const screenProps = yield call(getScreenPropsSaga);
    const analyticsProps = source ? { ...screenProps, source: source } : screenProps;
    datadogRum.addAction('@engage > workouts > sagas | deleteWorkout', {
      workoutId: workoutId,
    });
    yield call(deleteWorkout, client, workoutId, analyticsProps);
    yield put(removeWorkoutSuccess(workoutId));
  } catch (e) {
    logError(e, 'deleteWorkoutSaga');
  }
};

export const loadClassFromWorkoutSaga = function* (action: WorkoutRequestSuccessAction) {
  const workout = action.payload;
  if (isClassWorkout(workout)) {
    yield call(loadClassDetailSaga, workout.classId);
  }
};

export const workoutsSaga = function* (): SagaIterator {
  const client = yield getContext(CLIENT_CONTEXT);
  yield takeEvery(WorkoutActionType.Request, loadWorkoutSaga, client);
  yield takeEvery(WorkoutActionType.RequestSuccess, loadClassFromWorkoutSaga);
};

export const loadWorkoutAndClass = function* ({ workout, klass }: SplitResponse) {
  yield put(loadWorkoutSuccess(workout));
  if (klass) {
    yield put(loadClassSuccess(klass));
  }
};
